/**
 * Copyright © 2026 Adnuntius AS.
 *
 * Dependencies used by feature-state resolve factories. The instance belongs
 * to the router helper that builds those states; no module-global injector is
 * retained.
 */
import {Injector, Type} from '@angular/core';
import {StateService} from '@uirouter/core';

import {DataViewResource} from '../../components/api/resources/resource/data-view';
import {TimezonesResource} from '../../components/api/resources/resource/timezones';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {WorkspaceRegistryService} from '../../components/workspace-registry.service';

/**
 * Factory product behind empty resource DI tokens: ngResource-style API
 * (.get / .create / …). Tokens themselves are class shells with no methods.
 */
export type AdnNgResource = (new (...args: any[]) => any) & {
  get: (...args: any[]) => any;
  create: (...args: any[]) => any;
  query?: (...args: any[]) => any;
  getHydrated?: (...args: any[]) => any;
};

export class FeatureStateDependencies {
  constructor(private readonly injector: Injector) {
  }

  localUserProfile(): LocalUserProfile {
    return this.injector.get(LocalUserProfile);
  }

  localUserPermissions(): LocalUserPermissions {
    return this.injector.get(LocalUserPermissions);
  }

  localNetworkProfile(): LocalNetworkProfile {
    return this.injector.get(LocalNetworkProfile);
  }

  dataViewResource(): DataViewResource {
    return this.injector.get(DataViewResource);
  }

  timezonesResource(): TimezonesResource {
    return this.injector.get(TimezonesResource);
  }

  workspaceRegistry(): WorkspaceRegistryService {
    return this.injector.get(WorkspaceRegistryService);
  }

  state(): StateService {
    return this.injector.get(StateService);
  }

  /** Named resource string token (e.g. 'LineItem', 'Workspace') via Angular AliasProviders. */
  namedResource(token: string): AdnNgResource {
    return this.injector.get(token) as AdnNgResource;
  }

  /**
   * Resolve a resource DI token to its factory product. Do not treat the
   * token class type as the runtime shape.
   */
  resource(token: Type<unknown>): AdnNgResource {
    return this.injector.get(token) as AdnNgResource;
  }
}
